import { graphql, DataProps } from '@apollo/client/react/hoc';
import * as React from 'react';
import pluralize from 'pluralize';

import ConfirmDialog from 'components/ConfirmDialog';
import ErrorReporter from 'app/components/ErrorReporter';
import Group from 'entities/Group';
import GroupName from 'groups/components/GroupName';
import GroupSelectionDialog from 'groups/components/GroupSelectionDialog';
import { mountModal } from 'lib/modal';
import OwnedGroupSummariesFetcher from 'groups/containers/OwnedGroupSummariesFetcher';
import Relationship from 'entities/Relationship';
import Subscriber from 'entities/Subscriber';
import Text from 'components/Text';
import ToastActions from 'actions/ToastActions';

import ADD_MEMBERS_TO_GROUP_MUTATION from 'groups/components/mutations/addMembersToGroup.gql';

import type {
  addMembersToGroupVariables,
  addMembersToGroup_addUsersToGroup,
} from 'groups/components/mutations/__generated__/addMembersToGroup';
import { LoadOwnedGroupSummaries_me_classes } from 'groups/queries/__generated__/LoadOwnedGroupSummaries';
import type { ActiveGroupsQuery_organizations_classes_classes } from 'groups/containers/GroupList/queries/__generated__/ActiveGroupsQuery';

type Props = {
  getOwnedGroups?: boolean;
  orgUuid?: string;
  relationships: Relationship[];
  onSubscribe?: (groupUuid: string) => void;
  addMembersToGroup: (a: {
    variables: addMembersToGroupVariables;
  }) => Promise<DataProps<addMembersToGroup_addUsersToGroup>>;
  onRequestHide: () => void;
};

type State = {
  loadingModalOpen: boolean;
  query: string;
};

class CopySubscriberDialog extends React.Component<Props, State> {
  state: State = {
    loadingModalOpen: false,
    query: '',
  };

  getGroupsCopyableTo = (
    groups: ActiveGroupsQuery_organizations_classes_classes[] | LoadOwnedGroupSummaries_me_classes[]
  ): Group[] => {
    const { relationships } = this.props;
    const filteredGroups = (groups as any[]).filter(
      (
        group: ActiveGroupsQuery_organizations_classes_classes | LoadOwnedGroupSummaries_me_classes
      ) => {
        // filter out all groups where every user is already subscribed
        return relationships.every((relationship) => {
          const subscriptions = relationship.getSubscriptionsToMe();

          return !subscriptions.some((subscription) => {
            return subscription.getGroupId() === group.id && !group.staffClassOrgId;
          });
        });
      }
    );
    return filteredGroups.map(
      (
        group:
          | ActiveGroupsQuery_organizations_classes_classes[]
          | LoadOwnedGroupSummaries_me_classes[]
      ) => Group.fromJSON(group) as any as Group
    );
  };

  subscriberDisplayText = (subscribers: Subscriber[]) => {
    if (subscribers.length > 1) {
      return `${subscribers.length} users`;
    } else {
      return subscribers[0].getName();
    }
  };

  addSubscribers = (group: Group, subscribers: Subscriber[]) => {
    const { addMembersToGroup, onSubscribe, onRequestHide } = this.props;

    const userUuids = subscribers.map((sub) => sub.getUuid());
    const groupUuid = group.getUuid();
    const groupName = group.getName();

    this.setState({ loadingModalOpen: true });

    //
    addMembersToGroup({ variables: { input: { groupUuid, uuids: userUuids } } })
      .then(({ data }) => {
        // @ts-expect-error ts-migrate(2339) FIXME: Property 'addUsersToGroup' does not exist on type ... Remove this comment to see the full error message
        if (data.addUsersToGroup.error) {
          ToastActions.showErrorToast(
            `Unable to add ${this.subscriberDisplayText(subscribers)} to ${groupName}`
          );
        } else {
          ToastActions.showSuccessToast(
            `${this.subscriberDisplayText(subscribers)} ${pluralize(
              'was',
              subscribers.length
            )} added to ${groupName}`
          );

          if (onSubscribe && groupUuid) {
            onSubscribe(groupUuid);
          }
        }
      })
      .catch(() => {
        ToastActions.showErrorToast(
          `Unable to add ${this.subscriberDisplayText(subscribers)} to ${groupName}`
        );
      })
      .finally(() => {
        onRequestHide();
      });
  };

  handleQueryChange = (value: string) => {
    this.setState({ query: value });
  };

  confirmCopySubscriberToGroup = (group: Group) => {
    const { relationships } = this.props;
    const subscribers = relationships.map((relationship) => relationship.getRelatedUser());

    mountModal(
      <ConfirmDialog
        data-testid="confirm-copy-subscriber-to-group"
        eventScreenName="CopySubscriberDialog"
        title={
          <span>
            Are you sure you want to add{' '}
            <Text weight="semibold">{this.subscriberDisplayText(subscribers)}</Text> to{' '}
            <GroupName group={group} />?
          </span>
        }
        onConfirm={() => this.addSubscribers(group, subscribers)}
        actionButtonText="Add"
        cancelButtonText="Cancel"
      />,
      this
    );
  };

  renderEmptyMessage = (subscribers: Subscriber[], hasOwnedGroups: boolean) => {
    if (hasOwnedGroups) {
      return `${this.subscriberDisplayText(subscribers)} ${pluralize(
        'has',
        subscribers.length
      )} joined all of your classes.`;
    } else {
      return `You don't have any groups for ${this.subscriberDisplayText(
        subscribers
      )} to join yet.`;
    }
  };

  render() {
    const subscribers = this.props.relationships.map((relationship) =>
      relationship.getRelatedUser()
    );
    const { orgUuid, getOwnedGroups } = this.props;
    const { query } = this.state;

    return (
      <OwnedGroupSummariesFetcher orgUuid={orgUuid} query={query} getOwnedGroups={getOwnedGroups}>
        {({ groups, loading, error }) => {
          if (error) {
            return <ErrorReporter error="CopySubscriberDialog - could not fetch owned groups" />;
          }

          const hasOwnedGroups = groups ? groups.length > 0 : false;
          const emptyMessage = this.renderEmptyMessage(subscribers, hasOwnedGroups);

          return (
            <React.Fragment>
              <GroupSelectionDialog
                {...this.props}
                loading={loading}
                query={query}
                title={`Add ${this.subscriberDisplayText(subscribers)} to class`}
                groups={loading ? ([] as Group[]) : this.getGroupsCopyableTo(groups ? groups : [])}
                onSelect={this.confirmCopySubscriberToGroup}
                onQueryChange={this.handleQueryChange}
                emptyMessage={emptyMessage}
              />
            </React.Fragment>
          );
        }}
      </OwnedGroupSummariesFetcher>
    );
  }
}

const withAddMembersMutation = graphql(ADD_MEMBERS_TO_GROUP_MUTATION, {
  name: 'addMembersToGroup',
});

export { CopySubscriberDialog };
// @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'typeof CopySubscriberDialog' is ... Remove this comment to see the full error message
export default withAddMembersMutation(CopySubscriberDialog);
